/**
 * RecentTasksSection Component
 *
 * Card displaying recently added tasks with compact rows.
 * Each row shows status dot, title, due date, and priority badge.
 *
 * @module pages/DashboardPage/components/RecentTasksSection
 */

import { memo } from 'react';
import { Skeleton } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IconClipboardList, IconPlus } from '@tabler/icons-react';

import { ICON_SIZE_SM } from '@/constants/ui';
import { EmptyState, TableRowSkeleton } from '@/components/common';

import { CompactTaskRow } from '../CompactTaskRow';
import type { RecentTasksSectionProps } from './RecentTasksSection.types';
import styles from './RecentTasksSection.module.css';

/** Column flex values matching CompactTaskRow layout */
const SKELETON_COLUMNS = [
  { flex: 2.5 },
  { flex: 1.8 },
  { flex: 1 },
  { flex: 1 },
  { flex: 1.1 },
] as const;

export const RecentTasksSection = memo(function RecentTasksSection({
  tasks,
  isLoading,
}: RecentTasksSectionProps) {
  const { t } = useTranslation();

  const showSkeleton = isLoading && tasks.length === 0;

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <div className={styles.title}>
          <IconPlus size={ICON_SIZE_SM} />
          <span>{t('dashboard.recentTasks.title')}</span>
        </div>
        {showSkeleton ? (
          <Skeleton height={24} width={24} circle />
        ) : (
          <span className={styles.badge}>{tasks.length}</span>
        )}
      </div>
      {showSkeleton ? (
        <div className={styles.content}>
          <TableRowSkeleton columns={SKELETON_COLUMNS} count={5} showDot />
        </div>
      ) : tasks.length === 0 ? (
        <EmptyState
          size="sm"
          title={t('dashboard.emptyState.recentTasks')}
          icon={<IconClipboardList size={32} />}
        />
      ) : (
        <>
          <div className={styles.tableHeader}>
            <span className={styles.colDot} />
            <span className={styles.colTask}>
              {t('dashboard.recentTasks.columns.task')}
            </span>
            <span className={styles.colAssignee}>
              {t('dashboard.recentTasks.columns.assignee')}
            </span>
            <span className={styles.colPriority}>
              {t('dashboard.recentTasks.columns.priority')}
            </span>
            <span className={styles.colStatus}>
              {t('dashboard.recentTasks.columns.status')}
            </span>
            <span className={styles.colDueDate}>
              {t('dashboard.recentTasks.columns.dueDate')}
            </span>
          </div>
          <div className={styles.content}>
            {tasks.map((task) => (
              <CompactTaskRow key={task.id} task={task} />
            ))}
          </div>
        </>
      )}
    </section>
  );
});

RecentTasksSection.displayName = 'RecentTasksSection';
